import { PropsWithChildren, useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { usePermission } from '@backstage/plugin-permission-react';
import { useApi, appThemeApiRef } from '@backstage/core-plugin-api';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExtensionIcon from '@mui/icons-material/Extension';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import LibraryBooks from '@mui/icons-material/LibraryBooks';
import BuildIcon from '@mui/icons-material/Build';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CreateComponentIcon from '@mui/icons-material/AddCircleOutline';
import { PlaylistAdd as PlaylistAddIcon } from '@mui/icons-material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@mui/icons-material/Menu';
import { Search as SearchIcon, Settings as AdminPanelSettingsIcon, People as GroupIcon } from '@mui/icons-material';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { Shortcuts } from '@backstage-community/plugin-shortcuts';

const useSidebarLogoStyles = makeStyles()({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
    overflow: 'hidden',
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const { classes } = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div
      className={classes.root}
      style={isOpen ? { width: sidebarConfig.drawerWidthOpen, overflow: 'visible' } : {}}
    >
      <Link
        to="/"
        underline="none"
        className={classes.link}
        aria-label="Home"
        style={isOpen ? { width: sidebarConfig.drawerWidthOpen - 24 } : {}}
      >
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const ThemeToggle = () => {
  const appThemeApi = useApi(appThemeApiRef);
  const [isDark, setIsDark] = useState(appThemeApi.getActiveThemeId() === 'oneadvanced-dark');

  useEffect(() => {
    const subscription = appThemeApi.activeThemeId$().subscribe(id => {
      setIsDark(id === 'oneadvanced-dark');
    });
    return () => subscription.unsubscribe();
  }, [appThemeApi]);

  return (
    <SidebarItem
      icon={isDark ? Brightness7Icon : Brightness4Icon}
      text={isDark ? 'Light Mode' : 'Dark Mode'}
      onClick={() => appThemeApi.setActiveThemeId(isDark ? 'oneadvanced-light' : 'oneadvanced-dark')}
    />
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const { allowed: canRegister } = usePermission({ permission: catalogEntityCreatePermission });

  return (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={DashboardIcon} to="home" text="Overview" />
        <SidebarItem icon={HomeIcon} to="catalog" text="Catalog" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={TrackChangesIcon} to="tech-radar" text="Tech Radar" />
        <SidebarItem icon={CreateComponentIcon} to="workflows" text="Workflows" />
        <SidebarItem icon={QuestionAnswerIcon} to="faqs" text="FAQs" />
        <SidebarItem icon={HelpOutlineIcon} to="support" text="Support" />
        <SidebarItem icon={BuildIcon} to="tools" text="Tools" />
        {canRegister && <SidebarItem icon={PlaylistAddIcon} to="catalog-import" text="Register" />}
      </SidebarGroup>
      <SidebarDivider />
      <Shortcuts />
      <SidebarSpace />
      <SidebarDivider />
      
      <NotificationsSidebarItem />
      <SidebarDivider />
      <ThemeToggle />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
        {canRegister && (
          <SidebarItem icon={AdminPanelSettingsIcon} to="oneadvanced-admin" text="Admin Settings" />
        )}
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
  );
};
